import { createApp, defineAsyncComponent } from 'vue';
import { ua } from 'webmd-useragent/index.es6';
import metric from 'webmd-vuejs-metrics';
// eslint-disable-next-line import/no-extraneous-dependencies
import useStore from 'webmd-cms-store/src/store';
import axios from 'axios';
import getEnvStore from 'webmd-cms-store/src/store/helpers/utils.es6';
import shuffle from 'lodash/shuffle';
import oauth from 'webmd-oauth/src/index.es6';
import MedicalReference from '../Component.vue';

export default {
	created() {
		this.store = useStore(this.$pinia);
	},

	computed: {
		SlideShowRef() {
			return defineAsyncComponent(() => import(/* webpackChunkName: "load-dynamic" */'webmd-tool-slideshow/src/Component.vue'));
		},
		IACompRef() {
			return defineAsyncComponent(() => import(/* webpackChunkName: "load-dynamic" */'webmd-ia-module/src/Component.vue'));
		},
		isInfinite() {
			return this.store.flow.isInfinite;
		},
		getNextAssetSource() {
			const nextAssetSrc = this.store.flow.getNextAssetSource;
			return nextAssetSrc || '';
		},
	},
	methods: {

		/**
		 * getNextArticleData
		 * Set the next article ID, and dispatch event from the store to get the next article data
		 */

		getNextArticleData(nextAsset, cb) {
			const self = this;
			let capIdValue; let
				channelIdValue;

			if (nextAsset.busRef && nextAsset.busRef === 'Blog Post') {
				capIdValue = Object.values(window.infiniteList)[0].omniture.s_cap_id;
				channelIdValue = Object.values(window.infiniteList)[0].omniture.center_id;
			} else {
				capIdValue = nextAsset.capId;
				channelIdValue = nextAsset.fedConfig && nextAsset.fedConfig.programid
					? nextAsset.fedConfig.programid : nextAsset.channelId;
			}

			const newRoute = {
				params: {
					articleid: nextAsset.assetId,
					capid: capIdValue,
					channelid: channelIdValue,
				},
			};

			self.store.cms.getPageAndMetaData({ route: newRoute })
				.then(() => {
					self.store.flow.getFlowInfoForAsset({ route: newRoute })
						.then(() => {
							self.store.flow.setMetadata(self.infArticleNumber);
							cb();
						});
				});
		},

		loadNextArticle(nextArticle) {
			this.isMobile = ua.type === 'mobile' && window.matchMedia('(max-width: 466px)').matches;
			const self = this;
			const artNum = self.infArticleNumber;
			if (nextArticle && (nextArticle.contentType === 'Article' || nextArticle.contentType === 'Slideshow')) {
				const pageStartedEvent = new CustomEvent('infinite-page-started', {
					detail: {
						art: nextArticle.assetId,
						num: artNum,
					},
				});
				const iaCont = document.createElement('div');
				const nextArticleEl = document.createElement('div');
				self.$el.insertAdjacentElement('afterend', iaCont);
				iaCont.insertAdjacentElement('afterend', nextArticleEl);

				document.dispatchEvent(pageStartedEvent);

				if (!self.nextArticle.fedConfig && (nextArticle.busRef !== 'Blog Post')) {
					createApp(self.IACompRef, {
						articleNum: self.infArticleNumber,
					})
						.use(self.$pinia)
						.use(metric)
						.mount(iaCont);
				}

				self.getNextArticleData(nextArticle, () => {
					const pageLoadEvent = new CustomEvent('infinite-page-loaded', {
						detail: {
							art: nextArticle.assetId,
							num: self.infArticleNumber,
						},
					});

					window.initialLoad = true;
					if (nextArticle.contentType === 'Article') {
						const artcNum = self.infArticleNumber + 1;
						const isBlogArt = Boolean(nextArticle && nextArticle.busRef === 'Blog Post');

						createApp(MedicalReference, {
							chronicId: self.chronicId,
							fedConfig: self.nextArticle.fedConfig,
							isInteractive: false,
							nextArticleSource: self.iaSource,
							infArticleNumber: artcNum,
							isBlogArticle: isBlogArt,
							isFlowingFromNews: self.isSourceNews,
						})
							.use(self.$pinia)
							.use(metric)
							.mount(nextArticleEl);
					} else if (nextArticle.contentType === 'Slideshow') {
						const artNumber = self.infArticleNumber + 1;
						createApp(this.SlideShowRef, {
							chronicId: '091e9c5e8146bf61',
							isInitialLoad: true,
							fedConfig: self.nextArticle.fedConfig,
							medrefToss: true,
							nextArticleSource: self.iaSource,
							infArticleNumber: artNumber,
							isFlowingFromNews: self.isSourceNews,
						})
							.use(self.$pinia)
							.use(metric)
							.mount(nextArticleEl);
					}

					document.dispatchEvent(pageLoadEvent);
				});
			}
		},

		/**
		 * createArticleObserver
		 * Create intersection observer at the bottom of each article to change the
		 * url and history store as user scrolls up and down through articles
		 */
		createArticleObserver() {
			const self = this;
			const observer = new IntersectionObserver((entries) => {
				const entry = entries[0];

				// to check scroll direction and fire an event
				const currentY = entry.boundingClientRect.y;
				const currentRatio = entry.intersectionRatio;
				const { isIntersecting } = entry;

				// Scrolling down/up
				if (currentY < this.previousY) {
					if (currentRatio > this.previousRatio && isIntersecting && window.pageYOffset > 100) {
						self.handleIntersection(entry.target, 'down');
					}
				} else if (currentY > this.previousY && isIntersecting && window.pageYOffset > 100) {
					self.handleIntersection(entry.target, 'up');
				}

				this.previousY = currentY;
				this.previousRatio = currentRatio;
			});

			observer.observe(this.$el.closest('article'));
		},

		/**
		 * handleIntersection
		 * Observer handler
		 */
		handleIntersection(target, direction) {
			const self = this;
			const article = window.infiniteList[target.dataset.artid];
			const articleEl = target;
			const allArts = document.querySelectorAll('article');

			if (articleEl) {
				[...allArts].forEach((art) => art.classList.remove('current'));
				articleEl.classList.add('current');
			}

			if ((direction === 'down' || direction === 'up') && Object.keys(window.infiniteList).length > 1) {
				if (direction === 'down') {
					self.$vwmdTrack({
						moduleName: `pg-n-swipe_inf-${self.nextArticleSource}`,
					});
					document.addEventListener('onAdobeOmniBeforePv', () => {
						const omniVal = `inf-${self.artNum}-${self.iaSource}`;
						if (window && window.adobeDataLayer) {
							window.adobeDataLayer.push({ page: { ia: omniVal } });
						} else {
							window.s_md.contextData['wb.ia'] = omniVal;
						}
					}, { once: true });
				}
				this.updateUrl(article);
				this.updatePageData(article, direction);
				this.addHeightToAdContainers(direction);
				this.checkPromoModule();
			}
		},

		addHeightToAdContainers(direction) {
			[...document.querySelectorAll('.article-page .instreamAd')].forEach((el) => {
				const adHeight = `${el.offsetHeight}px`;
				if (el && el.children.length > 0 && direction === 'down') {
					// eslint-disable-next-line no-param-reassign
					el.style.minHeight = adHeight;
				} else if (direction === 'up') {
					// eslint-disable-next-line no-param-reassign
					el.style.minHeight = '280px';
				}
			});
		},

		checkPromoModule() {
			const promoMod = document.getElementsByClassName('promoWrapper');
			if (promoMod && promoMod.length > 0) {
				promoMod[0].style.display = 'none';
			}
		},

		/**
		 * updateUrl
		 * Update history (and url in address bar)
		 */
		updateUrl(article) {
			try {
				window.document.title = article.meta.page_title;
				if (article.omniture.s_furl && article.omniture.s_furl !== '') {
					if (article.omniture.s_business_reference === 'Blog Post') {
						window.history.replaceState({}, '', `/blogs${article.omniture.s_furl}`);
					} else {
						window.history.replaceState({}, '', article.omniture.s_furl);
					}
				}
			} catch (err) {
				// couldn't replace store
			}
		},

		/**
		 * updatePageData
		 * When new article is loaded, fire PV for page1 with updated metrics props
		 */
		updatePageData(articleProps, direction) {
			const self = this;

			self.store.cms.updatePagedata(articleProps);

			try {
				// eslint-disable-next-line no-global-assign
				window = Object.assign(window, self.store.pagedata.omniture);
			} catch (err) {
				// do nothing
			}

			try {
				if (direction === 'down') {
					self.$vwmdReset(true);
				} else {
					self.$vwmdReset(false);
				}
			} catch (err) {
				// do nothing
			}
		},

		/**
		 * triggerFooterModal
		 */
		triggerFooterModal() {
			const event = new Event('webmd.footer.overlay.open');
			document.dispatchEvent(event);
		},

		/**
		 * Gets next article recommendation from Adobe activity
		 * @returns {object} object with next asset
		 */
		// eslint-disable-next-line consistent-return
		async getNextAssetRec() {
			const self = this;
			const busRef = self.store.pagedata.omniture.s_business_reference.toLowerCase() || '';
			const topicVal = self.store.pagedata.omniture.s_topic;
			const language = self.store.pagedata.omniture.s_language_locale || 'en_US';
			const assetIdVal = self.store.pagedata.omniture.s_asset_id;
			const urlDomain = window.location.host.toLowerCase();
			const domainUrl = getEnvStore.getEnvironment(urlDomain).servicedomain;
			const apiUrl = `${domainUrl}/kapi/popular?topic=${topicVal}&lang=${language}` || '';

			// alloy article flow for infinite
			if (busRef.includes('news') || busRef === 'feature' || busRef === 'medical reference') {
				let dataObj = false;
				try {
					const response = await axios.get(apiUrl);
					const responsedataObj = response.data;
					const key = 'assetId';
					dataObj = [...new Map(responsedataObj.map((item) => [item[key], item])).values()];
					if (dataObj) {
						dataObj = shuffle(dataObj);
						if (dataObj[0].assetId === assetIdVal) {
							// eslint-disable-next-line prefer-destructuring
							dataObj = dataObj[1];
						} else {
							// eslint-disable-next-line prefer-destructuring
							dataObj = dataObj[0];
						}
						return {
							assetId: dataObj.assetId || '',
							capId: dataObj.capId || '',
							channelId: dataObj.channelId || '',
							contentType: dataObj.type || '',
							title: dataObj.title || '',
							url: dataObj.url || '',
						};
					}
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error('Error:', error);
				}
			} else if (busRef.includes('blog post')) {
				try {
					const adsValue = getEnvStore.getEnvironment(urlDomain).ads;
					const channelId = self.store.cms.friendlyURLData.target.centers.Micro.id;
					const env = adsValue === '1' ? 'staging.webmd.com' : 'webmd.com';
					const blogsNxtArtApi = `https://ec-blogs-api.k8s.${env}/kapi/v1/get-blogs-next-articles?q=channel_id:${channelId}%20AND%20-isdeleted:true&articleId=${assetIdVal}&count=1&start=0`;
					const oauthHeaders = oauth.generate();
					const response = await axios.get(blogsNxtArtApi, { headers: oauthHeaders });
					const blogArtData = response.data.data.items[0];

					if (blogArtData) {
						const blogArtId = blogArtData.id.replace('DCTM_', '');
						return {
							assetId: blogArtId || '',
							contentType: 'Article',
							title: blogArtData.title || '',
							url: blogArtData.url || '',
							busRef: 'Blog Post',
						};
					}
				} catch (error) {
					console.error('Error:', error);
				}
			} else {
				return false;
			}
		},

		/**
		 * Function to destroy a specific ad based on the ad id
		 */
		destroyAd(adid) {
			if (window.webmd.ads && window.webmd.ads.ads && window.webmd.ads.ads[adid]) {
				window.webmd.ads.destroy([adid]);
			}
		},
	},

	mounted() {
		this.previousY = 0;
		this.previousRatio = 0;
	},
};
